import React, { useState, useEffect, useRef, useCallback } from "react";
import "./OpsTicklesGame.css";
import { useLanguage } from "../contexts/LanguageContext";
import { useFirebase } from "../contexts/FirebaseContext";
import OpsTicklesHighScores from "./OpsTicklesHighScores";
import { isDemoMode, publishDemoState } from "../utils/demoMode";
import { saveOpsTicklesPointsScore } from "../services/opsTicklesScores";

const FIELD_WIDTH = 640;
const FIELD_HEIGHT = 360;
const JET_WIDTH = 56;
const JET_HEIGHT = 72;
const JET_Y = FIELD_HEIGHT - 80;
const JET_SPEED = 980;
const JET_NUDGE = 14;
const BULLET_SIZE = 18;
const BULLET_SPEED = 640;
const STAR_SIZE = 44;
const STAR_LABEL_H = 18;
const STAR_HIT_H = STAR_SIZE + STAR_LABEL_H;
const PLANE_W = 70;
const PLANE_H = 28;
const FIRE_COOLDOWN_MS = 180;
const MODE_COUNT = 7;
const HITS_PER_MODE = 5;

const MODE_LIMITS = [
  { fall: 38, spawn: 2600, planeChance: 0.12 },
  { fall: 48, spawn: 2200, planeChance: 0.16 },
  { fall: 60, spawn: 1900, planeChance: 0.2 },
  { fall: 74, spawn: 1600, planeChance: 0.24 },
  { fall: 90, spawn: 1350, planeChance: 0.28 },
  { fall: 108, spawn: 1120, planeChance: 0.32 },
  { fall: 128, spawn: 920, planeChance: 0.38 },
];

const targetForMode = (mode) => (mode + 1) * 5;

const randInt = (min, max) => min + Math.floor(Math.random() * (max - min + 1));

const pick = (list) => list[Math.floor(Math.random() * list.length)];

const makeMatchingExpr = (target) => {
  const options = [];
  const left = randInt(1, Math.max(1, target - 1));
  options.push(`${left}+${target - left}`);
  const left2 = randInt(1, Math.max(1, target - 1));
  options.push(`${left2}+${target - left2}`);
  const minus = randInt(1, 12);
  options.push(`${target + minus}-${minus}`);
  for (let factor = 2; factor <= 10; factor += 1) {
    if (target % factor === 0) {
      const other = target / factor;
      if (other >= 2 && other <= 12) {
        options.push(`${factor}×${other}`);
      }
    }
  }
  return { label: pick(options), value: target };
};

const makeDecoyExpr = (target) => {
  const offsets = [-5, -4, -3, -2, -1, 1, 2, 3, 4, 6, 7].filter(
    (n) => target + n > 0
  );
  for (let i = 0; i < 20; i += 1) {
    const value = target + pick(offsets);
    if (value === target || value < 1) continue;
    const roll = Math.random();
    if (roll < 0.7 && value >= 2) {
      const a = randInt(1, value - 1);
      return { label: `${a}+${value - a}`, value };
    }
    if (roll < 0.9) {
      const b = randInt(1, 9);
      return { label: `${value + b}-${b}`, value };
    }
    for (let factor = 2; factor <= 10; factor += 1) {
      if (value % factor === 0) {
        const other = value / factor;
        if (other >= 2 && other <= 10) {
          return { label: `${factor}×${other}`, value };
        }
      }
    }
    const a = randInt(1, Math.max(1, value - 1));
    return { label: `${a}+${value - a}`, value };
  }
  return { label: `${target}+1`, value: target + 1 };
};

const randomStarMath = (targetNumber) => {
  const targetChance = isDemoMode() ? 0.7 : 0.42;
  if (Math.random() < targetChance) return makeMatchingExpr(targetNumber);
  return makeDecoyExpr(targetNumber);
};

const StarShape = ({ className }) => (
  <svg className={className} viewBox="0 0 64 64" aria-hidden="true">
    <polygon points="32,4 39,24 60,24 43,36 50,56 32,44 14,56 21,36 4,24 25,24" />
  </svg>
);

const PlaneShape = () => (
  <svg className="ops-plane-svg" viewBox="0 0 80 32" aria-hidden="true">
    <path
      d="M6 18 L28 16 L38 4 L44 16 L74 18 L68 22 L46 21 L42 28 L34 28 L36 21 L12 22 L4 26 L2 24 Z"
      className="ops-plane-body"
    />
    <circle cx="22" cy="17" r="2.2" className="ops-plane-window" />
    <circle cx="28" cy="16.5" r="2" className="ops-plane-window" />
  </svg>
);

const OpsTicklesGame = ({ onPointsChange, autoStart = false }) => {
  const { t } = useLanguage();
  const { displayName, userId, recordGameEvent } = useFirebase();

  const [gameStarted, setGameStarted] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [score, setScore] = useState(0);
  const [finalScore, setFinalScore] = useState(null);
  const [jetX, setJetX] = useState(FIELD_WIDTH / 2 - JET_WIDTH / 2);
  const [stars, setStars] = useState([]);
  const [planes, setPlanes] = useState([]);
  const [bullets, setBullets] = useState([]);
  const [boardRefreshKey, setBoardRefreshKey] = useState(0);
  const [targetNumber, setTargetNumber] = useState(5);

  const animationFrameRef = useRef(null);
  const lastTimestampRef = useRef(null);
  const jetXRef = useRef(FIELD_WIDTH / 2 - JET_WIDTH / 2);
  const keysRef = useRef({ left: false, right: false });
  const starsRef = useRef([]);
  const planesRef = useRef([]);
  const bulletsRef = useRef([]);
  const scoreRef = useRef(0);
  const scoreSavedRef = useRef(false);
  const spawnTimerRef = useRef(0);
  const fireCooldownRef = useRef(0);
  const wantFireRef = useRef(false);
  const targetNumberRef = useRef(5);
  const modeRef = useRef(0);
  const hitsInModeRef = useRef(0);
  const didAutoStartRef = useRef(false);

  useEffect(() => {
    publishDemoState({
      game: "opsTickles",
      ready: gameStarted && !gameOver,
      gameOver,
      score,
      jetX,
      jetWidth: JET_WIDTH,
      jetHeight: JET_HEIGHT,
      fieldWidth: FIELD_WIDTH,
      fieldHeight: FIELD_HEIGHT,
      blockSize: STAR_SIZE,
      blockCount: stars.length,
      targetNumber,
      blocks: stars.map((b) => ({
        x: b.x,
        y: b.y,
        isTarget: b.isTarget,
        label: b.label,
      })),
    });
  }, [gameStarted, gameOver, score, jetX, stars, targetNumber]);

  const resetState = useCallback(() => {
    setGameStarted(true);
    setGameOver(false);
    setScore(0);
    setFinalScore(null);
    const startX = FIELD_WIDTH / 2 - JET_WIDTH / 2;
    setJetX(startX);
    jetXRef.current = startX;
    setStars([]);
    setPlanes([]);
    setBullets([]);
    starsRef.current = [];
    planesRef.current = [];
    bulletsRef.current = [];
    scoreRef.current = 0;
    scoreSavedRef.current = false;
    spawnTimerRef.current = 500;
    fireCooldownRef.current = 0;
    wantFireRef.current = false;
    modeRef.current = 0;
    hitsInModeRef.current = 0;
    const nextTarget = targetForMode(0);
    targetNumberRef.current = nextTarget;
    setTargetNumber(nextTarget);
    keysRef.current = { left: false, right: false };
    lastTimestampRef.current = null;
  }, []);

  useEffect(() => {
    if (!autoStart || didAutoStartRef.current) return;
    didAutoStartRef.current = true;
    resetState();
  }, [autoStart, resetState]);

  const endGame = useCallback(async () => {
    if (scoreSavedRef.current) return;
    const final = Math.floor(scoreRef.current);
    setGameOver(true);
    setFinalScore(final);
    scoreSavedRef.current = true;
    try {
      await saveOpsTicklesPointsScore({
        userId,
        displayName,
        points: final,
      });
    } catch (error) {
      console.error("Failed to save Ops-tickles score:", error);
    }
    setBoardRefreshKey((key) => key + 1);
    recordGameEvent("ops_tickles_ended", { score: final, points: final });
  }, [displayName, userId, recordGameEvent]);

  const fireBullet = useCallback(() => {
    if (fireCooldownRef.current > 0) return;
    const bullet = {
      id: crypto.randomUUID(),
      x: jetXRef.current + JET_WIDTH / 2 - BULLET_SIZE / 2,
      y: JET_Y - BULLET_SIZE + 6,
    };
    bulletsRef.current = [...bulletsRef.current, bullet];
    setBullets(bulletsRef.current);
    fireCooldownRef.current = FIRE_COOLDOWN_MS;
  }, []);

  const advanceModeIfNeeded = useCallback(() => {
    if (hitsInModeRef.current < HITS_PER_MODE) return;
    if (modeRef.current >= MODE_COUNT - 1) {
      hitsInModeRef.current = 0;
      return;
    }
    modeRef.current += 1;
    hitsInModeRef.current = 0;
    const nextTarget = targetForMode(modeRef.current);
    targetNumberRef.current = nextTarget;
    setTargetNumber(nextTarget);
    starsRef.current = [];
    planesRef.current = [];
    bulletsRef.current = [];
    setStars([]);
    setPlanes([]);
    setBullets([]);
    spawnTimerRef.current = 420;
  }, []);

  useEffect(() => {
    if (!gameStarted || gameOver) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      return;
    }

    let alive = true;
    lastTimestampRef.current = null;

    const update = (timestamp) => {
      if (!alive) return;
      if (scoreSavedRef.current) {
        animationFrameRef.current = null;
        return;
      }

      if (lastTimestampRef.current == null) {
        lastTimestampRef.current = timestamp;
        animationFrameRef.current = requestAnimationFrame(update);
        return;
      }

      const delta = (timestamp - lastTimestampRef.current) / 1000;
      lastTimestampRef.current = timestamp;
      const dt = Math.min(delta, 0.05);
      const demo = isDemoMode();
      const limits = MODE_LIMITS[modeRef.current] || MODE_LIMITS[MODE_COUNT - 1];
      const fallSpeed = limits.fall * (demo ? 1.45 : 1);
      const spawnInterval = Math.max(
        520,
        (demo ? limits.spawn * 0.7 : limits.spawn)
      );

      let nextX = jetXRef.current;
      if (keysRef.current.left && !keysRef.current.right) {
        nextX -= JET_SPEED * dt;
      } else if (keysRef.current.right && !keysRef.current.left) {
        nextX += JET_SPEED * dt;
      }
      nextX = Math.max(4, Math.min(FIELD_WIDTH - JET_WIDTH - 4, nextX));
      if (nextX !== jetXRef.current) {
        jetXRef.current = nextX;
        setJetX(nextX);
      }

      fireCooldownRef.current = Math.max(0, fireCooldownRef.current - dt * 1000);
      if (wantFireRef.current) {
        fireBullet();
      }

      spawnTimerRef.current -= dt * 1000;
      if (spawnTimerRef.current <= 0) {
        if (Math.random() < limits.planeChance) {
          const plane = {
            id: crypto.randomUUID(),
            x: 16 + Math.random() * (FIELD_WIDTH - PLANE_W - 32),
            y: -PLANE_H,
          };
          planesRef.current = [...planesRef.current, plane];
          setPlanes(planesRef.current);
        } else {
          const math = randomStarMath(targetNumberRef.current);
          const minX = 20;
          const maxX = FIELD_WIDTH - STAR_SIZE - 40;
          let x = minX + Math.random() * (maxX - minX);
          const mid = FIELD_WIDTH / 2;
          if (Math.abs(x + STAR_SIZE / 2 - mid) < 48) {
            x = x + STAR_SIZE / 2 < mid ? mid - 88 : mid + 44;
            x = Math.max(minX, Math.min(maxX, x));
          }
          const star = {
            id: crypto.randomUUID(),
            x,
            y: -STAR_HIT_H,
            label: math.label,
            value: math.value,
            isTarget: math.value === targetNumberRef.current,
          };
          starsRef.current = [...starsRef.current, star];
          setStars(starsRef.current);
        }
        spawnTimerRef.current = spawnInterval;
      }

      let nextBullets = bulletsRef.current
        .map((b) => ({ ...b, y: b.y - BULLET_SPEED * dt }))
        .filter((b) => b.y + BULLET_SIZE > 0);

      let nextStars = starsRef.current.map((star) => ({
        ...star,
        y: star.y + fallSpeed * dt,
      }));

      let nextPlanes = planesRef.current.map((plane) => ({
        ...plane,
        y: plane.y + fallSpeed * 0.92 * dt,
      }));

      const hitStarIds = new Set();
      const hitPlaneIds = new Set();
      const hitBulletIds = new Set();
      let shotWrongStar = false;
      let shotPlane = false;

      const overlaps = (ax, ay, aw, ah, bx, by, bw, bh) =>
        ax < bx + bw && ax + aw > bx && ay < by + bh && ay + ah > by;

      for (const bullet of nextBullets) {
        for (const star of nextStars) {
          if (hitStarIds.has(star.id)) continue;
          if (
            overlaps(
              bullet.x,
              bullet.y,
              BULLET_SIZE,
              BULLET_SIZE,
              star.x,
              star.y,
              STAR_SIZE,
              STAR_SIZE
            )
          ) {
            hitStarIds.add(star.id);
            hitBulletIds.add(bullet.id);
            if (star.isTarget || star.value === targetNumberRef.current) {
              scoreRef.current += 1;
              hitsInModeRef.current += 1;
            } else {
              shotWrongStar = true;
            }
            break;
          }
        }
        if (hitBulletIds.has(bullet.id)) continue;
        for (const plane of nextPlanes) {
          if (hitPlaneIds.has(plane.id)) continue;
          if (
            overlaps(
              bullet.x,
              bullet.y,
              BULLET_SIZE,
              BULLET_SIZE,
              plane.x,
              plane.y,
              PLANE_W,
              PLANE_H
            )
          ) {
            hitPlaneIds.add(plane.id);
            hitBulletIds.add(bullet.id);
            shotPlane = true;
            break;
          }
        }
      }

      if (hitStarIds.size > 0 || hitPlaneIds.size > 0) {
        nextStars = nextStars.filter((s) => !hitStarIds.has(s.id));
        nextPlanes = nextPlanes.filter((p) => !hitPlaneIds.has(p.id));
        nextBullets = nextBullets.filter((b) => !hitBulletIds.has(b.id));
        const newScore = Math.floor(scoreRef.current);
        if (newScore !== score) setScore(newScore);
      }

      const jetLeft = jetXRef.current;
      const jetRight = jetXRef.current + JET_WIDTH;
      const jetTop = JET_Y;
      const jetBottom = JET_Y + JET_HEIGHT;

      const collidedStar = nextStars.some(
        (star) =>
          star.x < jetRight - 8 &&
          star.x + STAR_SIZE > jetLeft + 8 &&
          star.y < jetBottom - 6 &&
          star.y + STAR_SIZE > jetTop + 10
      );
      const collidedPlane = nextPlanes.some(
        (plane) =>
          plane.x < jetRight - 6 &&
          plane.x + PLANE_W > jetLeft + 6 &&
          plane.y < jetBottom - 6 &&
          plane.y + PLANE_H > jetTop + 10
      );
      const missedTarget = nextStars.some(
        (star) => star.isTarget && star.y > FIELD_HEIGHT
      );

      nextStars = nextStars.filter((s) => s.y <= FIELD_HEIGHT);
      nextPlanes = nextPlanes.filter((p) => p.y <= FIELD_HEIGHT);

      starsRef.current = nextStars;
      planesRef.current = nextPlanes;
      bulletsRef.current = nextBullets;
      setStars(nextStars);
      setPlanes(nextPlanes);
      setBullets(nextBullets);

      if (shotWrongStar || shotPlane || collidedStar || collidedPlane || missedTarget) {
        endGame();
        animationFrameRef.current = null;
        return;
      }

      advanceModeIfNeeded();

      if (alive) {
        animationFrameRef.current = requestAnimationFrame(update);
      }
    };

    animationFrameRef.current = requestAnimationFrame(update);

    return () => {
      alive = false;
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameStarted, gameOver, fireBullet, endGame, advanceModeIfNeeded]);

  useEffect(() => {
    const applyNudge = (dir) => {
      if (!gameStarted || gameOver) return;
      const nextX = Math.max(
        4,
        Math.min(
          FIELD_WIDTH - JET_WIDTH - 4,
          jetXRef.current + dir * JET_NUDGE
        )
      );
      jetXRef.current = nextX;
      setJetX(nextX);
    };

    const setKey = (code, pressed) => {
      if (code === "ArrowLeft" || code === "KeyA") {
        keysRef.current.left = pressed;
        if (pressed) keysRef.current.right = false;
        return "left";
      }
      if (code === "ArrowRight" || code === "KeyD") {
        keysRef.current.right = pressed;
        if (pressed) keysRef.current.left = false;
        return "right";
      }
      if (code === "Space") {
        wantFireRef.current = pressed;
        return "fire";
      }
      return null;
    };

    const onKeyDown = (event) => {
      const kind = setKey(event.code, true);
      if (!kind) return;
      event.preventDefault();
      if (!gameStarted) {
        resetState();
        return;
      }
      if (event.repeat) return;
      if (kind === "left") applyNudge(-1);
      if (kind === "right") applyNudge(1);
      if (kind === "fire") fireBullet();
    };

    const onKeyUp = (event) => {
      setKey(event.code, false);
    };

    const onPointerUp = () => {
      wantFireRef.current = false;
      keysRef.current.left = false;
      keysRef.current.right = false;
    };

    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("keyup", onKeyUp, true);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("blur", onPointerUp);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("keyup", onKeyUp, true);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("blur", onPointerUp);
    };
  }, [gameStarted, gameOver, resetState, fireBullet]);

  const holdLeft = (pressed) => {
    keysRef.current.left = pressed;
    if (pressed) {
      keysRef.current.right = false;
      const nextX = Math.max(
        4,
        Math.min(FIELD_WIDTH - JET_WIDTH - 4, jetXRef.current - JET_NUDGE)
      );
      jetXRef.current = nextX;
      setJetX(nextX);
    }
  };
  const holdRight = (pressed) => {
    keysRef.current.right = pressed;
    if (pressed) {
      keysRef.current.left = false;
      const nextX = Math.max(
        4,
        Math.min(FIELD_WIDTH - JET_WIDTH - 4, jetXRef.current + JET_NUDGE)
      );
      jetXRef.current = nextX;
      setJetX(nextX);
    }
  };
  const holdFire = (pressed) => {
    wantFireRef.current = pressed;
    if (pressed && gameStarted && !gameOver) {
      fireBullet();
    }
  };

  return (
    <div className="ops-tickles-container">
      <div className="game-card ops-tickles-card">
        <div className="game-card-header">
          <h1 className="game-title">
            {gameOver ? t("gameOverTitle") : t("opsTickles")}
          </h1>
          {gameStarted && !gameOver && (
            <div className="ops-tickles-info">
              <div className="score-display-ops">
                <span className="score-label">{t("score")}</span>
                <span className="score-value">{score}</span>
              </div>
            </div>
          )}
        </div>

        {gameOver ? (
          <div className="ops-tickles-game-over">
            <div className="ops-final-score">
              <span className="ops-final-score-label">{t("finalScore")}</span>
              <span className="ops-final-score-value">{finalScore}</span>
            </div>
            <button className="new-game-button" onClick={resetState}>
              {t("playAgain") || t("startGame")}
            </button>
            <OpsTicklesHighScores refreshKey={boardRefreshKey} />
          </div>
        ) : (
          <div className="ops-tickles-play">
            <div
              className="ops-tickles-field"
              style={{ aspectRatio: `${FIELD_WIDTH} / ${FIELD_HEIGHT}` }}
            >
              <div className="ops-tickles-stars" aria-hidden="true" />
              {gameStarted && (
                <div
                  className="ops-target-burst"
                  title={t("opsTicklesTargetHint")}
                >
                  <svg viewBox="0 0 64 64" aria-hidden="true">
                    <polygon
                      points="32,2 36,16 50,8 44,22 62,22 46,32 62,42 44,42 50,56 36,48 32,62 28,48 14,56 20,42 2,42 18,32 2,22 20,22 14,8 28,16"
                    />
                  </svg>
                  <span className="ops-target-burst-value">{targetNumber}</span>
                </div>
              )}
              {stars.map((star) => (
                <div
                  key={star.id}
                  className="ops-math-star"
                  style={{
                    left: `${(star.x / FIELD_WIDTH) * 100}%`,
                    top: `${(star.y / FIELD_HEIGHT) * 100}%`,
                    width: `${(STAR_SIZE / FIELD_WIDTH) * 100}%`,
                    height: `${(STAR_HIT_H / FIELD_HEIGHT) * 100}%`,
                  }}
                >
                  <StarShape className="ops-math-star-svg" />
                  <span className="ops-math-star-label">{star.label}</span>
                </div>
              ))}
              {planes.map((plane) => (
                <div
                  key={plane.id}
                  className="ops-plane"
                  style={{
                    left: `${(plane.x / FIELD_WIDTH) * 100}%`,
                    top: `${(plane.y / FIELD_HEIGHT) * 100}%`,
                    width: `${(PLANE_W / FIELD_WIDTH) * 100}%`,
                    height: `${(PLANE_H / FIELD_HEIGHT) * 100}%`,
                  }}
                >
                  <PlaneShape />
                </div>
              ))}
              {bullets.map((bullet) => (
                <div
                  key={bullet.id}
                  className="ops-star-bullet"
                  style={{
                    left: `${(bullet.x / FIELD_WIDTH) * 100}%`,
                    top: `${(bullet.y / FIELD_HEIGHT) * 100}%`,
                    width: `${(BULLET_SIZE / FIELD_WIDTH) * 100}%`,
                    height: `${(BULLET_SIZE / FIELD_HEIGHT) * 100}%`,
                  }}
                >
                  <StarShape className="ops-star-bullet-svg" />
                </div>
              ))}
              {gameStarted && (
                <div
                  className="ops-rocket"
                  style={{
                    left: `${(jetX / FIELD_WIDTH) * 100}%`,
                    top: `${(JET_Y / FIELD_HEIGHT) * 100}%`,
                    width: `${(JET_WIDTH / FIELD_WIDTH) * 100}%`,
                    height: `${(JET_HEIGHT / FIELD_HEIGHT) * 100}%`,
                  }}
                  aria-hidden="true"
                >
                  <svg viewBox="0 0 64 80" className="ops-rocket-svg">
                    <defs>
                      <linearGradient
                        id="ops-rocket-body"
                        x1="0%"
                        y1="0%"
                        x2="100%"
                        y2="0%"
                      >
                        <stop offset="0%" stopColor="#cbd5e1" />
                        <stop offset="45%" stopColor="#f8fafc" />
                        <stop offset="100%" stopColor="#94a3b8" />
                      </linearGradient>
                      <linearGradient
                        id="ops-rocket-fin"
                        x1="0%"
                        y1="0%"
                        x2="0%"
                        y2="100%"
                      >
                        <stop offset="0%" stopColor="var(--secondary-color)" />
                        <stop offset="100%" stopColor="var(--primary-color-strong)" />
                      </linearGradient>
                      <linearGradient
                        id="ops-flame-core"
                        x1="0%"
                        y1="0%"
                        x2="0%"
                        y2="100%"
                      >
                        <stop offset="0%" stopColor="#fff7ed" />
                        <stop offset="40%" stopColor="#fb923c" />
                        <stop offset="100%" stopColor="#ef4444" />
                      </linearGradient>
                    </defs>
                    <g className="ops-rocket-flame">
                      <path
                        className="ops-rocket-flame-outer"
                        d="M26 58 Q32 78 38 58 Z"
                        fill="url(#ops-flame-core)"
                        opacity="0.85"
                      />
                      <path
                        className="ops-rocket-flame-inner"
                        d="M29 58 Q32 72 35 58 Z"
                        fill="#fef3c7"
                      />
                    </g>
                    <path d="M18 44 L8 62 L22 54 Z" fill="url(#ops-rocket-fin)" />
                    <path d="M46 44 L56 62 L42 54 Z" fill="url(#ops-rocket-fin)" />
                    <path
                      d="M32 4 C42 16 44 34 42 54 L22 54 C20 34 22 16 32 4 Z"
                      fill="url(#ops-rocket-body)"
                    />
                    <path
                      d="M32 8 C38 18 39 32 38 48 L26 48 C25 32 26 18 32 8 Z"
                      fill="rgba(255,255,255,0.35)"
                    />
                    <circle cx="32" cy="28" r="7" fill="var(--primary-color)" />
                    <circle cx="32" cy="28" r="4.5" fill="var(--secondary-color)" />
                    <circle cx="30" cy="26" r="1.6" fill="#e0f2fe" />
                    <path d="M32 4 L36 14 L28 14 Z" fill="#ef4444" />
                    <rect x="24" y="54" width="16" height="5" rx="2" fill="#334155" />
                  </svg>
                </div>
              )}
            </div>

            <div className="ops-tickles-controls">
              <p className="ops-tickles-hint">{t("opsTicklesHint")}</p>
              <div className="ops-tickles-buttons">
                <button
                  type="button"
                  className="ops-control-btn ops-control-btn--left"
                  onPointerDown={(e) => {
                    e.preventDefault();
                    if (!gameStarted) resetState();
                    holdLeft(true);
                  }}
                  onPointerUp={() => holdLeft(false)}
                  onPointerLeave={() => holdLeft(false)}
                  onPointerCancel={() => holdLeft(false)}
                  aria-label="Left"
                >
                  <svg viewBox="0 0 40 48" aria-hidden="true">
                    <polygon points="38,2 2,24 38,46" />
                  </svg>
                </button>
                <button
                  type="button"
                  className="ops-control-btn ops-control-btn--right"
                  onPointerDown={(e) => {
                    e.preventDefault();
                    if (!gameStarted) resetState();
                    holdRight(true);
                  }}
                  onPointerUp={() => holdRight(false)}
                  onPointerLeave={() => holdRight(false)}
                  onPointerCancel={() => holdRight(false)}
                  aria-label="Right"
                >
                  <svg viewBox="0 0 40 48" aria-hidden="true">
                    <polygon points="2,2 38,24 2,46" />
                  </svg>
                </button>
                <button
                  type="button"
                  className="ops-control-btn ops-control-btn--fire"
                  onPointerDown={(e) => {
                    e.preventDefault();
                    if (!gameStarted) {
                      resetState();
                    }
                    holdFire(true);
                  }}
                  onPointerUp={() => holdFire(false)}
                  onPointerLeave={() => holdFire(false)}
                  onPointerCancel={() => holdFire(false)}
                  aria-label={t("opsTicklesShoot")}
                >
                  <span aria-hidden="true">^</span>
                  <span aria-hidden="true">^</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {!gameStarted && !gameOver && (
          <div className="ops-tickles-instructions">
            <p>
              {t("opsTicklesInstructions")
                .split("\n")
                .map((line, index, arr) => (
                  <React.Fragment key={index}>
                    {line}
                    {index < arr.length - 1 && <br />}
                  </React.Fragment>
                ))}
            </p>
            <button className="new-game-button" onClick={resetState}>
              {t("startGame")}
            </button>
            <div className="ops-tickles-board-wrap">
              <OpsTicklesHighScores refreshKey={boardRefreshKey} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OpsTicklesGame;
